import React, { useCallback, useEffect, useMemo, useState } from "react";
import { analyticsApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import {
  BarChart3,
  MessageSquare,
  PieChart,
  Clock,
  Database,
  AlertCircle,
  RefreshCw,
  Download,
  ShoppingCart,
} from "lucide-react";

const RANGE_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "custom", label: "Custom Range" },
];

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString() : "0");
const formatDateLabel = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toDateString();
};

const SectionCard = ({ title, children, icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      {Icon ? <Icon className="w-5 h-5 text-purple-600" /> : null}
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const StatTile = ({ label, value, helper }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {helper ? <p className="text-xs text-gray-500 mt-1">{helper}</p> : null}
  </div>
);

const VerticalBar = ({ label, value, max }) => {
  const height = max > 0 ? `${Math.round((value / max) * 100)}%` : "0%";
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full h-48 bg-gray-100 rounded-t-lg overflow-hidden flex items-end">
        <div
          className="w-full bg-purple-500 rounded-t-lg transition-all duration-300"
          style={{ height }}
        ></div>
      </div>
      <div className="mt-2 text-center">
        <p className="text-xs text-gray-600 font-medium truncate max-w-full px-1">{label}</p>
        <p className="text-sm font-semibold text-gray-900">{formatNumber(value)}</p>
      </div>
    </div>
  );
};

const DateFilter = ({ selected, onChange, customFrom, customTo, onCustomChange }) => (
  <div className="flex flex-wrap gap-2 items-center">
    {RANGE_OPTIONS.map((option) => (
      <button
        key={option.key}
        onClick={() => onChange(option.key)}
        className={`px-3 py-2 rounded-md border text-sm ${
          selected === option.key
            ? "bg-purple-600 text-white border-purple-600"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        }`}
      >
        {option.label}
      </button>
    ))}
    {selected === "custom" ? (
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={customFrom}
          onChange={(e) => onCustomChange(e.target.value, customTo)}
          className="px-3 py-2 border rounded-md text-sm"
        />
        <span className="text-gray-500 text-sm">to</span>
        <input
          type="date"
          value={customTo}
          onChange={(e) => onCustomChange(customFrom, e.target.value)}
          className="px-3 py-2 border rounded-md text-sm"
        />
      </div>
    ) : null}
  </div>
);

const Analytics = () => {
  const { user } = useAuth();
  const userRole = user?.role || "admin";
  const isSuperAdmin = userRole === "super-admin" || userRole === "super_admin";

  const [range, setRange] = useState("7d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [messages, setMessages] = useState(null);
  const [plugins, setPlugins] = useState([]);
  const [queries, setQueries] = useState(null);
  const [buttonClicks, setButtonClicks] = useState(null);
  const [trends, setTrends] = useState(null);
  const [itemOrderCount, setItemOrderCount] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const params = useMemo(() => {
    const base = { range };
    if (range === "custom") {
      return { ...base, from: customFrom, to: customTo };
    }
    return base;
  }, [range, customFrom, customTo]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (isSuperAdmin) {
        // Super Admin: fetch all analytics + item order count
        const [
          overviewRes,
          sessionRes,
          messageRes,
          pluginRes,
          queryRes,
          buttonClickRes,
          trendRes,
          itemOrderRes,
        ] = await Promise.all([
          analyticsApi.getOverview(params),
          analyticsApi.getSessions(params),
          analyticsApi.getMessages(params),
          analyticsApi.getPlugins(params),
          analyticsApi.getQueries(params),
          analyticsApi.getButtonClicks(params),
          analyticsApi.getTrends(params),
          analyticsApi.getItemOrderCount(params),
        ]);

        setOverview(overviewRes.data);
        setSessions(sessionRes.data);
        setMessages(messageRes.data);
        setPlugins(pluginRes.data || []);
        setQueries(queryRes.data);
        setButtonClicks(buttonClickRes.data);
        setTrends(trendRes.data);
        setItemOrderCount(itemOrderRes.data);
      } else {
        // Admin: fetch only item order count
        const itemOrderRes = await analyticsApi.getItemOrderCount(params);
        setItemOrderCount(itemOrderRes.data);
      }
    } catch (err) {
      console.error("Analytics fetch error:", err);
      const errorMessage = err?.response?.data?.error || err?.message || "Failed to load analytics";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [params, isSuperAdmin]);

  const handleExportCsv = async () => {
    try {
      setExportLoading(true);
      await analyticsApi.getItemOrderCountExport(params);
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (range === "custom" && (!customFrom || !customTo)) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, range, customFrom, customTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-purple-200 border-t-purple-600 rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-center rounded-lg p-6">
        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  // Admin: check if itemOrderCount is loaded; Super Admin: check overview
  const hasData = isSuperAdmin ? overview != null : itemOrderCount != null;
  if (!hasData) {
    return (
      <div className="bg-blue-50 border border-blue-200 text-center rounded-lg p-6">
        <BarChart3 className="w-10 h-10 text-blue-500 mx-auto mb-3" />
        <p className="text-blue-700">No analytics data available yet.</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Load Data
        </button>
      </div>
    );
  }

  const sessionPeak =
    sessions?.sessionsPerDay?.reduce(
      (max, row) => Math.max(max, row.sessionStarts + row.sessionEnds),
      0,
    ) || 0;
  const messagePeak =
    messages?.messagesPerDay?.reduce((max, row) => Math.max(max, row.count), 0) || 0;
  const pluginPeak = plugins.reduce((max, row) => Math.max(max, row.executions || 0), 0) || 0;
  const buttonClickPeak =
    buttonClicks?.byButton?.reduce((max, row) => Math.max(max, row.clicks || 0), 0) || 0;
  const itemQueries = queries?.items || queries?.product;
  const itemOrderPeak =
    itemOrderCount?.items?.reduce((max, row) => Math.max(max, row.count || 0), 0) || 0;

  // Item Order Count section (visible to Admin and Super Admin)
  const ItemOrderCountSection = () => (
    <SectionCard title="Item Order Count" icon={ShoppingCart}>
      <div className="flex justify-end mb-4">
        <button
          onClick={handleExportCsv}
          disabled={exportLoading}
          className="px-3 py-2 bg-purple-600 text-white text-sm rounded-md inline-flex items-center gap-2 hover:bg-purple-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exportLoading ? "Exporting..." : "Export CSV"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatTile
          label="Total Order Clicks"
          value={formatNumber(itemOrderCount?.totalClicks || 0)}
        />
        <StatTile
          label="Unique Items Ordered"
          value={itemOrderCount?.items?.length || 0}
          helper="Different items clicked"
        />
        <StatTile
          label="Avg Clicks/Day"
          value={
            itemOrderCount?.clicksPerDay?.length > 0
              ? Math.round(
                  (itemOrderCount?.totalClicks || 0) / itemOrderCount.clicksPerDay.length,
                ).toLocaleString()
              : "0"
          }
          helper="Daily average"
        />
      </div>
      <div className="space-y-4">
        {itemOrderCount?.items && itemOrderCount.items.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {(() => {
                const MIN_ITEMS = 10;
                const topItems = itemOrderCount.items.slice(0, MIN_ITEMS);
                const padded = [...topItems];
                while (padded.length < MIN_ITEMS) {
                  padded.push({
                    itemId: `placeholder-${padded.length}`,
                    itemName: "—",
                    count: 0,
                  });
                }
                return padded.map((item) => (
                  <VerticalBar
                    key={item.itemId}
                    label={item.itemName || `Item ${item.itemId}`}
                    value={item.count}
                    max={itemOrderPeak}
                  />
                ));
              })()}
            </div>
            {itemOrderCount.items.length > 10 && (
              <p className="text-sm text-gray-500 text-center">
                Showing top 10 of {itemOrderCount.items.length} items. Export CSV for full list.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">No item order data.</p>
        )}
      </div>
    </SectionCard>
  );

  // Admin: show only Item Order Count
  if (!isSuperAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Item Order Analytics</h2>
            <p className="text-sm text-gray-600">
              Track how many times items have been ordered via the chatbot.
            </p>
          </div>
          <DateFilter
            selected={range}
            onChange={setRange}
            customFrom={customFrom}
            customTo={customTo}
            onCustomChange={(from, to) => {
              setCustomFrom(from);
              setCustomTo(to);
            }}
          />
        </div>
        <ItemOrderCountSection />
      </div>
    );
  }

  // Super Admin: show all analytics including Item Order Count
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Usage Analytics</h2>
          <p className="text-sm text-gray-600">
            Track conversations, plugins, and button interactions.
          </p>
        </div>
        <DateFilter
          selected={range}
          onChange={setRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={(from, to) => {
            setCustomFrom(from);
            setCustomTo(to);
          }}
        />
      </div>

      <SectionCard title="Session Statistics" icon={BarChart3}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <StatTile label="Total Sessions" value={formatNumber(overview.totalSessions)} />
          <StatTile label="Total Messages" value={formatNumber(overview.totalMessages)} />
          <StatTile
            label="Avg Messages / Session"
            value={(overview.avgMessagesPerSession || 0).toFixed(1)}
            helper="Engagement depth"
          />
          <StatTile
            label="User vs Bot"
            value={`${formatNumber(messages?.userMessages || 0)} / ${formatNumber(messages?.botMessages || 0)}`}
            helper="User / Bot"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {sessions?.sessionsPerDay && sessions.sessionsPerDay.length > 0 ? (
            sessions.sessionsPerDay.map((row) => (
              <VerticalBar
                key={row.date}
                label={formatDateLabel(row.date)}
                value={row.sessionStarts + row.sessionEnds}
                max={sessionPeak}
              />
            ))
          ) : (
            <p className="text-sm text-gray-500">No session data.</p>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Message Volume" icon={MessageSquare}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <StatTile label="User Messages" value={formatNumber(messages?.userMessages || 0)} />
          <StatTile label="Bot Messages" value={formatNumber(messages?.botMessages || 0)} />
          <StatTile
            label="Histogram Buckets"
            value={messages?.histogram?.length || 0}
            helper="Sessions grouped"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-4">
          {messages?.messagesPerDay && messages.messagesPerDay.length > 0 ? (
            messages.messagesPerDay.map((row) => (
              <VerticalBar
                key={row.date}
                label={formatDateLabel(row.date)}
                value={row.count}
                max={messagePeak}
              />
            ))
          ) : (
            <p className="text-sm text-gray-500">No message data.</p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {messages?.histogram?.map((bucket) => (
            <StatTile
              key={bucket.bucket}
              label={`Msgs ${bucket.bucket}`}
              value={formatNumber(bucket.count)}
            />
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Plugin Usage" icon={Database}>
        {plugins && plugins.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {plugins.map((plugin) => (
                <VerticalBar
                  key={plugin.plugin}
                  label={plugin.plugin}
                  value={plugin.executions}
                  max={pluginPeak}
                />
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {plugins.map((plugin) => (
                <StatTile
                  key={plugin.plugin}
                  label={`${plugin.plugin} share`}
                  value={`${Math.round((plugin.share || 0) * 100)}%`}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No plugin usage data.</p>
        )}
      </SectionCard>

      <SectionCard title="Query Analytics" icon={PieChart}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <StatTile
            label="Item Queries"
            value={`${formatNumber(itemQueries?.matched || 0)} matched / ${formatNumber(
              itemQueries?.unmatched || 0,
            )}`}
          />
          <StatTile
            label="Store Queries"
            value={`${formatNumber(queries?.store?.matched || 0)} matched / ${formatNumber(
              queries?.store?.unmatched || 0,
            )}`}
          />
          <StatTile label="Menu Selections" value={formatNumber(queries?.menu || 0)} />
          <StatTile label="RAG Queries" value={formatNumber(queries?.rag || 0)} />
        </div>
      </SectionCard>

      <SectionCard title="Button Click Analytics" icon={Clock}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <StatTile
            label="Total Button Clicks"
            value={formatNumber(buttonClicks?.totalClicks || 0)}
          />
          <StatTile
            label="Unique Buttons"
            value={buttonClicks?.byButton?.length || 0}
            helper="Different buttons used"
          />
          <StatTile
            label="Avg Clicks/Day"
            value={
              buttonClicks?.clicksPerDay?.length > 0
                ? Math.round(
                    buttonClicks.totalClicks / buttonClicks.clicksPerDay.length,
                  ).toLocaleString()
                : "0"
            }
            helper="Daily average"
          />
        </div>
        <div className="space-y-4">
          {buttonClicks?.byButton && buttonClicks.byButton.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {buttonClicks.byButton.map((button) => (
                  <VerticalBar
                    key={button.buttonKey}
                    label={button.buttonLabel}
                    value={button.clicks}
                    max={buttonClickPeak}
                  />
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                {buttonClicks.byButton.map((button) => (
                  <StatTile
                    key={button.buttonKey}
                    label={`${button.buttonLabel} share`}
                    value={`${Math.round((button.share || 0) * 100)}%`}
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">No button click data.</p>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Usage Trends" icon={AlertCircle}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p className="font-semibold text-sm mb-2">Sessions</p>
            <div className="space-y-1 text-xs text-gray-700">
              {trends?.sessions && trends.sessions.length > 0 ? (
                trends.sessions.map((row) => (
                  <div key={row.date} className="flex justify-between">
                    <span>{formatDateLabel(row.date)}</span>
                    <span className="font-semibold">{formatNumber(row.count)}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No data</p>
              )}
            </div>
          </div>
          <div>
            <p className="font-semibold text-sm mb-2">Messages</p>
            <div className="space-y-1 text-xs text-gray-700">
              {trends?.messages && trends.messages.length > 0 ? (
                trends.messages.map((row) => (
                  <div key={row.date} className="flex justify-between">
                    <span>{formatDateLabel(row.date)}</span>
                    <span className="font-semibold">{formatNumber(row.count)}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No data</p>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Item Order Count - also visible to Super Admin */}
      <ItemOrderCountSection />
    </div>
  );
};

export default Analytics;
